import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, unref as _unref, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "flex flex-col items-center justify-center py-8 gap-3",
  "data-testid": "approval-flow-load-error"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", { class: "text-red-500 text-sm" }, " Não foi possível carregar as configurações de excepcionalidade. ", -1)
const _hoisted_3 = { key: 2 }
const _hoisted_4 = { class: "flex items-center justify-between mb-6" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h2", { class: "text-xl font-bold leading-6 tracking-tight text-primary50 text-left" }, " Configurações de excepcionalidade ", -1)
const _hoisted_6 = { class: "flex gap-2" }
const _hoisted_7 = {
  class: "flex items-center gap-2 px-3 py-2 border border-primary30 rounded bg-primary5 mb-4",
  "data-testid": "info-operation-role"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm text-secondary50" }, " Os papéis validadores Operação e Análise de Risco são obrigatórios no fluxo de validação de excepcionalidade, e não podem ser deletados ", -1)
const _hoisted_9 = {
  key: 0,
  class: "flex items-center gap-2 px-3 py-2 border border-warning rounded bg-[#FEF9E7] mb-4",
  "data-testid": "alert-flow-normalized"
}
const _hoisted_10 = /*#__PURE__*/_createElementVNode("span", { class: "text-sm text-secondary50" }, " Esta configuração foi atualizada para incluir os papéis obrigatórios. Salve para aplicar a mudança. ", -1)

import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiAlertOutline, mdiFormTextboxLock } from '@mdi/js'

import ApprovalLayerCard from '@/components/ManageScale/Schedule/components/ApprovalLayerCard.vue'
import ApprovalFlowPreview from '@/components/ManageScale/Schedule/ApprovalFlowPreview.vue'
import ApprovalFlowSkeleton from '@/components/designSystem/Skeleton/ApprovalFlowSkeleton.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ImportExceptionalityConfigModal from '@/components/designSystem/modals/ImportExceptionalityConfigModal/ImportExceptionalityConfigModal.vue'
import SaveApprovalTemplateModal from '@/components/designSystem/modals/SaveApprovalTemplateModal/SaveApprovalTemplateModal.vue'

import { useApprovalFlowStore } from '@/pinia-store/useApprovalFlowStore'
import type { ApprovalGroup } from '@/types/approval-flow.types'
import { LayerOperator } from '@/types/approval-flow.types'
import type { ApprovalLayerDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'
import {
  apiLayersToGroup,
  createEmptyGroup,
  groupToApiLayers,
  allRulesHaveRoles,
  ensureMandatoryRoles
} from '@/utils/approval-flow-transformers'
import { useImportApprovalFlow } from '@/hooks/useImportApprovalFlow'


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleApprovalFlowTab',
  props: {
    scheduleId: {},
    companyId: {}
  },
  emits: ["update:isSaving"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;




const toast = useToast()
const { notifyError } = useErrorToast()
const approvalFlowStore = useApprovalFlowStore()
const { importConfig } = useImportApprovalFlow()

const approvalGroup = ref<ApprovalGroup>(createEmptyGroup())
const flowId = ref<number | null>(null)
const isLoading = ref(false)
const isError = ref(false)
const showErrors = ref(false)
const originalSnapshot = ref('')
// Fluxo legado corrigido na carga: some assim que o usuario salvar.
const wasNormalizedOnLoad = ref(false)
const importModalRef = ref<InstanceType<typeof ImportExceptionalityConfigModal> | null>(null)
const isImportModalOpen = ref(false)
const isSaveTemplateModalOpen = ref(false)
const isSavingTemplate = ref(false)
const savedTemplate = ref<{ id: number; name: string } | null>(null)

const handleImportConfig = async (payload: { source: 'template' | 'scale'; id: number }) => {
  const imported = await importConfig(payload)
  if (imported) {
    approvalGroup.value = imported
    isImportModalOpen.value = false
    importModalRef.value?.onImportSuccess()
  } else {
    isImportModalOpen.value = false
  }
  savedTemplate.value = null
}

const handleImportClose = () => {
  isImportModalOpen.value = false
  savedTemplate.value = null
}

const handleSaveTemplate = async (payload: { name: string; companyId: number }) => {
  isSavingTemplate.value = true
  try {
    const layers = groupToApiLayers(approvalGroup.value)
    const created = await approvalFlowStore.createTemplate({
      name: payload.name,
      companyId: payload.companyId,
      scheduleId: props.scheduleId,
      layers
    })
    savedTemplate.value = { id: created.id, name: created.name }
    isSaveTemplateModalOpen.value = false
    isImportModalOpen.value = true
    toast.success('Modelo salvo com sucesso.')
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível salvar o modelo de configuração.')
  } finally {
    isSavingTemplate.value = false
  }
}

const previewLayers = computed<ApprovalLayerDetail[]>(() => {
  const apiLayers = groupToApiLayers(approvalGroup.value)
  return apiLayers.map((layer, idx) => ({
    id: idx,
    order: layer.order,
    operator: layer.operator ?? LayerOperator.AND,
    roles: (layer.roles ?? []).map((r, rIdx) => ({
      id: rIdx,
      role: r.role,
      weight: r.weight ?? rIdx + 1
    }))
  }))
})
const hasUnsavedChanges = computed(() => {
  if (!originalSnapshot.value) return false
  return JSON.stringify(approvalGroup.value) !== originalSnapshot.value
})

async function loadFlow() {
  isLoading.value = true
  isError.value = false

  try {
    await approvalFlowStore.fetchByScheduleId(props.scheduleId)

    if (approvalFlowStore.currentFlow) {
      flowId.value = approvalFlowStore.currentFlow.id

      // O snapshot tem de sair do grupo CRU, antes da normalização. Tirá-lo
      // depois zerava `hasUnsavedChanges`: a tela mostrava os papéis
      // obrigatórios, o banco continuava sem eles, o guard de saída não avisava
      // e a correção só existia enquanto o modal estivesse aberto.
      const persisted = apiLayersToGroup(approvalFlowStore.currentFlow.layers)
      originalSnapshot.value = JSON.stringify(persisted)
      approvalGroup.value = ensureMandatoryRoles(persisted)
      wasNormalizedOnLoad.value = hasUnsavedChanges.value
    } else {
      flowId.value = null
      approvalGroup.value = createEmptyGroup()
      originalSnapshot.value = JSON.stringify(approvalGroup.value)
      wasNormalizedOnLoad.value = false
    }
  } catch (error: unknown) {
    isError.value = true
    notifyError(error, 'Não foi possível carregar as configurações de excepcionalidade.')
  } finally {
    isLoading.value = false
  }
}

async function handleSave() {
  if (!allRulesHaveRoles(approvalGroup.value)) {
    showErrors.value = true
    toast.error('Todas as regras devem ter um papel validador.')
    return
  }

  showErrors.value = false
  emit('update:isSaving', true)

  try {
    const layers = groupToApiLayers(approvalGroup.value)

    if (flowId.value === null) {
      await approvalFlowStore.createFlow({ scheduleId: props.scheduleId, layers })
      flowId.value = approvalFlowStore.currentFlow?.id ?? null
    } else {
      await approvalFlowStore.updateFlow(flowId.value, { scheduleId: props.scheduleId, layers })
    }

    originalSnapshot.value = JSON.stringify(approvalGroup.value)
    wasNormalizedOnLoad.value = false
    toast.success('Excepcionalidade salva com sucesso.')
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível salvar as configurações de excepcionalidade.')
  } finally {
    emit('update:isSaving', false)
  }
}

onMounted(() => loadFlow())

__expose({ handleSave, hasUnsavedChanges })

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(ApprovalFlowSkeleton, { key: 0 }))
      : (isError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _hoisted_2,
            _createVNode(OutlinedButton, {
              text: "Tentar novamente",
              width: "160px",
              height: "36px",
              onClick: loadFlow
            })
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _hoisted_5,
              _createElementVNode("div", _hoisted_6, [
                _createVNode(OutlinedButton, {
                  text: "Salvar como modelo",
                  width: "auto",
                  height: "36px",
                  "data-testid": "save-template-button",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (isSaveTemplateModalOpen.value = true))
                }),
                _createVNode(OutlinedButton, {
                  text: "Importar configurações",
                  width: "auto",
                  height: "36px",
                  "data-testid": "import-exceptionality-button",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (isImportModalOpen.value = true))
                })
              ])
            ]),
            _createVNode(ImportExceptionalityConfigModal, {
              ref_key: "importModalRef",
              ref: importModalRef,
              "modal-active": isImportModalOpen.value,
              "saved-template": savedTemplate.value,
              "schedule-id": props.scheduleId,
              "company-id": props.companyId,
              onClose: handleImportClose,
              onSave: handleImportConfig
            }, null, 8, ["modal-active", "saved-template", "schedule-id", "company-id"]),
            _createVNode(SaveApprovalTemplateModal, {
              "modal-active": isSaveTemplateModalOpen.value,
              layers: previewLayers.value,
              loading: isSavingTemplate.value,
              onClose: _cache[2] || (_cache[2] = ($event: any) => (isSaveTemplateModalOpen.value = false)),
              onSave: handleSaveTemplate
            }, null, 8, ["modal-active", "layers", "loading"]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_component_v_icon, {
                icon: _unref(mdiFormTextboxLock),
                size: "18",
                class: "text-primary30 shrink-0"
              }, null, 8, ["icon"]),
              _hoisted_8
            ]),
            (wasNormalizedOnLoad.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(_component_v_icon, {
                    icon: _unref(mdiAlertOutline),
                    size: "18",
                    class: "text-warning shrink-0"
                  }, null, 8, ["icon"]),
                  _hoisted_10
                ]))
              : _createCommentVNode("", true),
            _createVNode(ApprovalLayerCard, {
              group: approvalGroup.value,
              "show-errors": showErrors.value,
              "onUpdate:group": _cache[3] || (_cache[3] = (updated) => (approvalGroup.value = updated))
            }, null, 8, ["group", "show-errors"]),
            (previewLayers.value.length > 0)
              ? (_openBlock(), _createBlock(ApprovalFlowPreview, {
                  key: 1,
                  layers: previewLayers.value,
                  class: "mt-4"
                }, null, 8, ["layers"]))
              : _createCommentVNode("", true)
          ]))
  ]))
}
}

})